import type { ReactNode } from 'react';
import type { Compared } from '../period/compare';
import { miniTrend } from './mini-trend';

export interface TrendPanelProps {
  /** The number's name with its period: "Sign-ups, last 7 days". */
  label: string;
  /** The period's total, formatted. */
  total: string;
  compared: Compared;
  current: readonly number[];
  previous: readonly number[];
  /** What a hover says the number means, when a word in it has one definition. */
  help?: string | undefined;
}

const W = 240;
const H = 44;

/** A small panel: the total large, the period as a line over the one before (one scale), and how it moved. */
export function TrendPanel(props: TrendPanelProps): ReactNode {
  const { label, total, compared, current, previous } = props;
  const lines = miniTrend(current, previous, W, H);
  const flat = current.every((v) => v === 0) && previous.every((v) => v === 0);
  return (
    <span className="ad-tpanel pui-chart-frame" title={props.help}>
      <span className="ad-tpanel__label">{label}</span>
      <strong className="ad-tpanel__total">{total}</strong>
      <span className="ad-tpanel__compare">
        <span className={`ad-tone--${compared.tone}`}>{compared.change}</span> {compared.before}
      </span>
      <svg
        className="ad-tpanel__trend"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={`${label}: ${current.join(', ')}; ${compared.before}: ${previous.join(', ')}`}
      >
        {flat ? (
          <line className="ad-tpanel__flat" x1={0} x2={W} y1={H - 2} y2={H - 2} />
        ) : (
          <>
            <polyline className="ad-tpanel__previous" points={lines.previous} />
            <polyline className="ad-tpanel__current" points={lines.current} />
          </>
        )}
      </svg>
    </span>
  );
}
